/** お題の頁（㊳ 写真、㊵ クイズ、㊷b 掲示する）。帖より一回り小さい紙を台紙の上に重ねる */
import type { StatePrompt } from '../../api/types';
import { answerQuiz, createListing } from '../../api/rpc';
import { toRpcError } from '../../api/errors';
import { geo } from '../../core/geo';
import { remainingMs } from '../../core/clock';
import { deckle } from '../../core/deckle';
import { openView } from '../../core/viewlog';
import type { TabContext } from '../contract';
import type { GameState } from '../../api/types';
import { btn, cameraIcon, h, swapIcon } from './dom';
import { mini } from './exchange';
import {
  type AreaInfo,
  anyTitle,
  areaMap,
  canPost,
  defaultWant,
  errorText,
  giveChoices,
  quizWaitText,
  type Want,
  wantRows,
} from './logic';
import type { Ticker } from './ticker';
import type { Bundle } from './logic';

export interface PagerEnv {
  ctx: TabContext;
  ticker: Ticker;
  state(): GameState | null;
  /** 手に入ったお題を、お題選択に戻ったときに絵葉書にする */
  markFresh(ids: number[]): void;
  rememberAnswer(promptId: number, text: string): void;
  onClosed(): void;
}

export class Pager {
  readonly el = h('div', 'pr-fa');
  private pg = h('div', 'pr-pg');
  private closeView: ((reason?: string) => void) | null = null;
  private viewClosed = false;
  private busy = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private kind: 'photo' | 'quiz' | 'post' | null = null;

  constructor(private env: PagerEnv) {
    this.el.setAttribute('role', 'dialog');
    this.el.appendChild(this.pg);
  }

  get isOpen(): boolean {
    return this.kind !== null;
  }

  get isBusy(): boolean {
    return this.busy;
  }

  /** いま開いている頁のお題 id（写真・クイズの頁のときだけ） */
  currentPromptId: number | null = null;

  private begin(kind: 'photo' | 'quiz' | 'post', hex: string, label: string, cls: string): HTMLElement {
    this.finish('close');
    this.kind = kind;
    this.busy = false;
    this.el.className = `pr-fa ${cls} pr-on`;
    this.el.style.setProperty('--c', hex);
    this.el.setAttribute('aria-label', label);
    while (this.pg.firstChild) this.pg.removeChild(this.pg.firstChild);
    const inn = h('div', 'pr-in');
    this.pg.appendChild(inn);
    // 耳付きの縁（README「紙の端」）。表示してから大きさを測る
    requestAnimationFrame(() => this.cut());
    return inn;
  }

  cut(): void {
    if (this.el.offsetWidth > 0) this.el.style.setProperty('--deckle', deckle(this.el.offsetWidth, this.el.offsetHeight, 7717));
  }

  private startView(promptId: number): void {
    this.viewClosed = false;
    this.closeView = null;
    void openView('prompt', promptId, 'prompts').then((f) => {
      if (this.viewClosed) f('close');
      else this.closeView = f;
    });
  }

  private endView(reason: string): void {
    this.viewClosed = true;
    this.closeView?.(reason);
    this.closeView = null;
  }

  /** 頁を閉じる（閲覧ログも閉じる）。refresh などは呼ばない */
  private finish(reason: string): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    if (this.kind === 'photo' || this.kind === 'quiz') this.endView(reason);
    this.kind = null;
    this.currentPromptId = null;
    this.el.classList.remove('pr-on');
  }

  close(reason = 'close'): void {
    if (this.busy && this.kind !== 'quiz') return;
    const was = this.kind;
    this.finish(reason);
    if (was) this.env.onClosed();
  }

  private backButton(label: string, onClick: () => void): HTMLElement {
    return btn('pr-back2', label, onClick);
  }

  private header(kindText: string, areaName: string, title: string): HTMLElement {
    const hd = h('div', 'pr-hd');
    const k = h('span', 'pr-kind', `${kindText}・${areaName}`);
    hd.appendChild(k);
    hd.appendChild(h('p', 'pr-ttl', title));
    return hd;
  }

  // ------------------------------------------------------------ 写真（㊳）

  openPhoto(p: StatePrompt, b: Bundle): void {
    const inn = this.begin('photo', b.hex, '写真のお題', 'pr-fphoto');
    this.pg.insertBefore(this.backButton('お題選択に戻る', () => this.close('close')), inn);
    this.currentPromptId = p.prompt_id;
    this.startView(p.prompt_id);

    inn.appendChild(this.header('写真のお題', b.area.name, p.title));
    const note = h('p', 'pr-note');
    if (p.hint) note.textContent = p.hint;
    if (p.hint) inn.appendChild(note);
    const pic = h('div', 'pr-pic');
    const shoot = h('button', 'pr-btn pr-pri pr-shoot', undefined, { type: 'button' });
    shoot.appendChild(cameraIcon());
    shoot.appendChild(document.createTextNode('写真を撮る'));
    pic.appendChild(shoot);
    inn.appendChild(pic);
    const err = h('p', 'pr-err');
    inn.appendChild(err);

    shoot.addEventListener('click', async () => {
      if (this.busy) return;
      this.busy = true;
      shoot.setAttribute('aria-disabled', 'true');
      err.textContent = '';
      try {
        // カメラ・確認・送信中・失敗の画面は提出の流れが持つ。提出したら閲覧ログは流れの側で閉じる
        const out = await this.env.ctx.submitPhoto(this.el, {
          kind: 'prompt',
          targetId: p.prompt_id,
          title: p.title,
          areaId: p.area_id,
          closeView: (reason) => this.endView(reason ?? 'submitted'),
        });
        if (out.status === 'sent') {
          this.busy = false;
          this.finish('submitted');
          this.env.markFresh([p.prompt_id]);
          this.env.onClosed();
          await this.env.ctx.refresh();
          return;
        }
        if (out.status === 'queued') {
          this.busy = false;
          this.finish('queued');
          this.env.onClosed();
          return;
        }
        if (out.status === 'rejected') err.textContent = errorText(out.error.code);
      } catch (e) {
        err.textContent = errorText(toRpcError(e).code);
      } finally {
        this.busy = false;
        shoot.removeAttribute('aria-disabled');
      }
    });
  }

  // ------------------------------------------------------------ クイズ（㊵）

  openQuiz(p: StatePrompt, b: Bundle): void {
    const inn = this.begin('quiz', b.hex, 'クイズ', 'pr-fq');
    this.pg.insertBefore(this.backButton('お題選択に戻る', () => this.leaveQuiz()), inn);
    this.currentPromptId = p.prompt_id;
    this.startView(p.prompt_id);

    inn.appendChild(this.header('クイズ', b.area.name, p.title));
    inn.appendChild(h('p', 'pr-rule2', '間違えると5分間はどのクイズも答えられません。'));
    const opts = h('div', 'pr-opts');
    const choices = p.choices ?? [];
    let pick = 0;
    const buttons = choices.map((c, i) => {
      const o = h('button', 'pr-opt', c, { type: 'button', 'aria-pressed': 'false' });
      o.addEventListener('click', () => {
        if (o.getAttribute('aria-disabled') || this.busy) return;
        pick = i + 1;
        buttons.forEach((x, j) => x.setAttribute('aria-pressed', j === i ? 'true' : 'false'));
        go.removeAttribute('aria-disabled');
      });
      opts.appendChild(o);
      return o;
    });
    // 選択肢のないクイズは体験型（023）。体験のあとに教わる4桁の数字を打ち込む
    const codeIn = p.choices === null ? h('input', 'pr-code', undefined, {
      type: 'text', inputmode: 'numeric', autocomplete: 'off', 'aria-label': '4桁の数字',
    }) : null;
    if (codeIn) {
      codeIn.placeholder = '4桁の数字';
      codeIn.addEventListener('input', () => {
        codeIn.value = codeIn.value.replace(/\D/g, '').slice(0, 4);
        pick = /^[1-9]\d{3}$/.test(codeIn.value) ? Number(codeIn.value) : 0;
        if (pick) go.removeAttribute('aria-disabled');
        else go.setAttribute('aria-disabled', 'true');
      });
      opts.classList.add('pr-opts-code');
      opts.appendChild(codeIn);
    }
    inn.appendChild(opts);

    const ng = h('div', 'pr-ng-box');
    ng.appendChild(h('strong', 'pr-verdict', '不正解'));
    const ngp = h('p');
    inn.appendChild(ng);
    const ok = h('div', 'pr-ok-box');
    ok.appendChild(h('strong', 'pr-verdict', '正解'));
    inn.appendChild(ok);
    inn.appendChild(h('span', 'pr-sp'));
    const act = h('div', 'pr-act');
    const go = h('button', 'pr-btn pr-pri', undefined, { type: 'button', 'aria-disabled': 'true' });
    const lb = h('span', undefined, 'この答えで答える');
    go.appendChild(lb);
    go.appendChild(h('i', 'pr-bar'));
    act.appendChild(go);
    inn.appendChild(act);
    const err = h('p', 'pr-err');
    inn.appendChild(err);

    const showWait = (untilIso: string, wrongIndex: number | null): void => {
      this.el.classList.add('pr-bad');
      buttons.forEach((x) => x.setAttribute('aria-disabled', 'true'));
      if (wrongIndex !== null) buttons[wrongIndex]?.classList.add('pr-wrong');
      if (codeIn) {
        codeIn.disabled = true;
        if (wrongIndex !== null) codeIn.classList.add('pr-wrong');
      }
      while (ngp.firstChild) ngp.removeChild(ngp.firstChild);
      const t = h('b');
      ngp.appendChild(t);
      ngp.appendChild(document.createTextNode(' 後に答えられます'));
      ng.appendChild(ngp);
      this.env.ticker.add(t, untilIso, () => {
        // 答えられる時刻になった
        this.el.classList.remove('pr-bad');
        buttons.forEach((x) => {
          x.removeAttribute('aria-disabled');
          x.classList.remove('pr-wrong');
          x.setAttribute('aria-pressed', 'false');
        });
        if (codeIn) {
          codeIn.disabled = false;
          codeIn.classList.remove('pr-wrong');
          codeIn.value = '';
        }
        pick = 0;
        go.setAttribute('aria-disabled', 'true');
      });
    };
    if (p.retry_at && remainingMs(p.retry_at) > 0) showWait(p.retry_at, null);

    go.addEventListener('click', async () => {
      if (go.getAttribute('aria-disabled') || this.busy || pick === 0) return;
      this.busy = true;
      err.textContent = '';
      lb.textContent = '答え合わせをしています';
      this.el.classList.add('pr-sending');
      go.setAttribute('aria-disabled', 'true');
      buttons.forEach((x) => x.setAttribute('aria-disabled', 'true'));
      if (codeIn) codeIn.disabled = true;
      const chosen = pick;
      try {
        const f = geo.fresh();
        const r = await answerQuiz({
          promptId: p.prompt_id,
          choice: chosen,
          clientActionAt: new Date().toISOString(),
          lat: f?.lat ?? null,
          lon: f?.lon ?? null,
          acc: f?.acc ?? null,
        });
        this.el.classList.remove('pr-sending');
        lb.textContent = 'この答えで答える';
        if (r.correct) {
          this.env.rememberAnswer(p.prompt_id, codeIn ? String(chosen) : (choices[chosen - 1] ?? ''));
          this.el.classList.add('pr-ok');
          // 「正解」を見せて1.2秒で自動でお題選択に戻り、紙片に切手と消印を押す
          this.timer = setTimeout(() => void this.finishQuiz(p.prompt_id, true), 1200);
        } else {
          showWait(r.retry_at, chosen - 1);
          void this.env.ctx.refresh();
        }
      } catch (e) {
        const code = toRpcError(e).code;
        this.el.classList.remove('pr-sending');
        lb.textContent = 'この答えで答える';
        err.textContent = errorText(code);
        buttons.forEach((x) => x.removeAttribute('aria-disabled'));
        if (codeIn) codeIn.disabled = false;
        go.removeAttribute('aria-disabled');
        if (code === 'quiz_cooldown' || code === 'already_have') void this.env.ctx.refresh();
      } finally {
        this.busy = false;
      }
    });
    void quizWaitText;
  }

  private leaveQuiz(): void {
    if (this.el.classList.contains('pr-sending')) return;
    const id = this.currentPromptId;
    // 正解を見せている間に戻ったときも、そのまま絵葉書にする
    if (this.el.classList.contains('pr-ok') && id !== null) void this.finishQuiz(id, true);
    else this.close('close');
  }

  private async finishQuiz(promptId: number, correct: boolean): Promise<void> {
    if (this.kind !== 'quiz') return;
    this.finish(correct ? 'answered' : 'close');
    if (correct) this.env.markFresh([promptId]);
    this.env.onClosed();
    await this.env.ctx.refresh();
  }

  // ------------------------------------------------------------ 掲示する（㊷b）

  openPost(): void {
    const s = this.env.state();
    if (!s) return;
    const inn = this.begin('post', '#2e2116', '掲示する', 'pr-fp');
    this.pg.insertBefore(this.backButton('交換所に戻る', () => this.close('close')), inn);
    const areas = areaMap(s);

    let give: number | null = null;
    let want: Want | null = null;
    const dw = defaultWant(s);
    if (dw !== null) want = { promptId: dw };

    // 上の見本：もらう ⇄ 渡す。選んだ絵葉書がその場で入る
    const pv = h('div', 'pr-pv');
    const how = h('div', 'pr-how');
    how.appendChild(h('span', undefined, 'もらう'));
    how.appendChild(swapIcon());
    how.appendChild(h('span', undefined, '渡す'));
    pv.appendChild(how);
    const slots = h('div', 'pr-pp pr-big');
    pv.appendChild(slots);
    inn.appendChild(pv);

    const fmw = h('div', 'pr-fmw');
    const fm = h('div', 'pr-fm');
    fm.appendChild(h('p', 'pr-lb2', 'もらう'));
    const wantBtns: { b: HTMLElement; w: Want }[] = [];
    for (const row of wantRows(s)) {
      const r = h('div', 'pr-arow');
      const an = h('span', 'pr-an2', row.area.name);
      r.appendChild(an);
      const chs = h('div', 'pr-chs');
      const info = areas.get(row.area.area_id);
      an.style.setProperty('--c', info?.hex ?? '#b9a27a');
      for (const pr of row.prompts) {
        chs.appendChild(this.chip(pr.title, info, false, { promptId: pr.prompt_id }, wantBtns, () => pickWant));
      }
      chs.appendChild(this.chip(anyTitle(row.area.name), info, true, { areaId: row.area.area_id }, wantBtns, () => pickWant));
      r.appendChild(chs);
      fm.appendChild(r);
    }
    fm.appendChild(h('p', 'pr-lb2', '渡す'));
    const giveBtns: { b: HTMLElement; id: number }[] = [];
    const gchs = h('div', 'pr-chs');
    for (const c of giveChoices(s)) {
      const info = areas.get(c.area_id);
      const b = h('button', 'pr-ch', c.title, { type: 'button', 'aria-pressed': 'false' });
      b.style.setProperty('--c', info?.hex ?? '#b9a27a');
      b.addEventListener('click', () => {
        give = c.prompt_id;
        giveBtns.forEach((x) => x.b.setAttribute('aria-pressed', x.id === give ? 'true' : 'false'));
        update();
      });
      giveBtns.push({ b, id: c.prompt_id });
      gchs.appendChild(b);
    }
    if (giveBtns.length === 0) gchs.appendChild(h('p', 'pr-note', '交換に出せる写真がありません。'));
    fm.appendChild(gchs);
    fmw.appendChild(fm);
    inn.appendChild(fmw);

    const err = h('p', 'pr-err');
    inn.appendChild(err);
    const go = h('button', 'pr-btn pr-pri pr-postgo', undefined, { type: 'button', 'aria-disabled': 'true' });
    const lb = h('span', undefined, '掲示する');
    go.appendChild(lb);
    go.appendChild(h('i', 'pr-bar'));
    inn.appendChild(go);

    const pickWant = (w: Want): void => {
      want = w;
      wantBtns.forEach((x) => x.b.setAttribute('aria-pressed', sameWant(x.w, w) ? 'true' : 'false'));
      update();
    };
    const update = (): void => {
      while (slots.firstChild) slots.removeChild(slots.firstChild);
      const wi = want?.areaId ? areas.get(want.areaId) : areas.get(s.prompts.find((x) => x.prompt_id === want?.promptId)?.area_id ?? '');
      const wt = want ? (want.areaId ? anyTitle(wi?.name ?? '') : (s.prompts.find((x) => x.prompt_id === want?.promptId)?.title ?? '')) : '';
      const gc = s.hand.find((x) => x.prompt_id === give);
      slots.appendChild(mini({ title: wt, area: want ? wi : undefined, owned: false, hideArea: !!want?.areaId }));
      slots.appendChild(swapIcon());
      slots.appendChild(mini({ title: gc?.title ?? '', area: gc ? areas.get(gc.area_id) : undefined, owned: !!gc }));
      if (canPost(give, want)) go.removeAttribute('aria-disabled');
      else go.setAttribute('aria-disabled', 'true');
    };
    if (want) {
      const w0 = want;
      wantBtns.forEach((x) => x.b.setAttribute('aria-pressed', sameWant(x.w, w0) ? 'true' : 'false'));
    }
    update();

    go.addEventListener('click', async () => {
      if (go.getAttribute('aria-disabled') || this.busy || give === null || !want) return;
      this.busy = true;
      go.setAttribute('aria-disabled', 'true');
      lb.textContent = '掲示しています';
      this.el.classList.add('pr-sending');
      err.textContent = '';
      try {
        await createListing({ promptId: give, wantPromptId: want.promptId ?? null, wantAreaId: want.areaId ?? null });
        this.busy = false;
        this.el.classList.remove('pr-sending');
        this.finish('close');
        this.env.onClosed();
        await this.env.ctx.refresh();
      } catch (e) {
        err.textContent = errorText(toRpcError(e).code);
        lb.textContent = '掲示する';
        this.el.classList.remove('pr-sending');
        update();
        void this.env.ctx.refresh();
      } finally {
        this.busy = false;
      }
    });
  }

  private chip(
    title: string,
    info: AreaInfo | undefined,
    any: boolean,
    w: Want,
    list: { b: HTMLElement; w: Want }[],
    pick: () => (w: Want) => void,
  ): HTMLElement {
    const b = h('button', any ? 'pr-ch pr-any' : 'pr-ch', title, { type: 'button', 'aria-pressed': 'false' });
    b.style.setProperty('--c', info?.hex ?? '#b9a27a');
    b.addEventListener('click', () => pick()(w));
    list.push({ b, w });
    return b;
  }
}

function sameWant(a: Want, b: Want): boolean {
  return a.promptId === b.promptId && a.areaId === b.areaId;
}
